import { message, Modal, Spin, Tag } from 'antd';
import { TextService } from '../../Services/TextService';
import { EmailContentResultDto, WebApiEmailDtoCommunicationReportDto } from '../../Repository/eventbookyapi';
import Table, { ColumnsType } from 'antd/lib/table';
import { formatDateString } from '../../Helpers/DateHelpers';
import { EmailEventType } from './EmailEventType';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import { useState } from 'react';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { MailOutlined } from '@ant-design/icons';

const AdminEmailLogsTable = (props: any) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState<WebApiEmailDtoCommunicationReportDto | null>(null);
    const [emailContent, setEmailContent] = useState<EmailContentResultDto | null>(null);

    const [mailContentLoading, setMailContentLoading] = useState(false);
    const showModal = async (log: WebApiEmailDtoCommunicationReportDto) => {
        setSelectedLog(log);
        setIsModalVisible(true);

        setMailContentLoading(true);

        // Fetch email content
        try {
            const response = await EmailTemplateService.GetMailContent({
                messageId: log.event?.messageId!,
                email: log.event?.email!,
                eventDate: log.event?.date,
            });
            setEmailContent(response); // Assuming `response.content` contains the email content
        } catch (error) {
            message.error("Failed to fetch email content.");
        } finally {
            setMailContentLoading(false);
        }
    };

    const handleOk = () => {
        setIsModalVisible(false);
    };

    const GetText = TextService.GetText;

    const columns: ColumnsType<WebApiEmailDtoCommunicationReportDto> = [
        {
            title: '',
            key: 'history',
            width: 50,
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                log.event!.email!.indexOf("@") != -1 ? <MailOutlined style={{ cursor: 'pointer', fontSize: '16px' }} onClick={() => showModal(log)} /> : null
            ),
        },
        {
            title: GetText("EmailLogs_SentDate"),
            key: 'sentDate',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                <span>{formatDateString(log.event!.date!.toString())}</span>
            ),
        },
        {
            title: GetText("EmailLogs_Email"),
            key: 'email',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                <span>{log.event!.email}</span>
            ),
        },
        {
            title: GetText("EmailLogs_Subject"),
            key: 'subject',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                <span>{log.event!.subject}</span>
            ),
        },
        {
            title: GetText("EmailLogs_From"),
            key: 'from',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                <span>{log.event!.from}</span>
            ),
        },
        {
            title: GetText("EmailLogs_LatestStatus"),
            key: 'latestStatus',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => {
                const status = (log.latestStatus ?? log.event!.event) as EmailEventType;
                return <>
                    <Tag color={mapColor(status)}>{mapText(status)}</Tag>
                    {log.latestStatusDate && log.latestStatus !== log.event!.event ? <div style={{ fontSize: 12 }}>{formatDateString(log.latestStatusDate.toString())}</div> : null}
                </>;
            },
        },
        {
            title: GetText("EmailLogs_Reason"),
            key: 'reason',
            render: (_, log: WebApiEmailDtoCommunicationReportDto) => (
                <span>{log.latestReason ?? log.event!.reason}</span>
            ),
        }
    ];

    const mapColor = (event: EmailEventType) => {
        switch (event) {
            case EmailEventType.Delivered:
            case EmailEventType.Opened:
            case EmailEventType.Clicks:
            case EmailEventType.Accepted:
            case EmailEventType.Replies:
                return "green";
            case EmailEventType.Bounces:
            case EmailEventType.HardBounces:
            case EmailEventType.SoftBounces:
            case EmailEventType.Spam:
            case EmailEventType.Deferred:
            case EmailEventType.Blocked:
            case EmailEventType.Unsubscribed:
            case EmailEventType.Unsubscription:
            case EmailEventType.Skipped:
                return "orange";
            case EmailEventType.Error:
            case EmailEventType.Invalid:
            case EmailEventType.Rejected:
                return "red";
            default:
                return "blue"
        }
    }

    const mapText = (event: EmailEventType) => {
        switch (event) {
            case EmailEventType.Requests:
            case EmailEventType.Sent:
                return GetText("EmailLogs_Requests");
            case EmailEventType.Delivered:
                return GetText("EmailLogs_Delivered");
            case EmailEventType.Opened:
                return GetText("EmailLogs_Opened");
            case EmailEventType.Clicks:
                return GetText("EmailLogs_Clicks");
            case EmailEventType.Bounces:
                return GetText("EmailLogs_Bounce");
            case EmailEventType.HardBounces:
                return GetText("EmailLogs_HardBounces");
            case EmailEventType.SoftBounces:
                return GetText("EmailLogs_SoftBounces");
            case EmailEventType.Spam:
                return GetText("EmailLogs_Spam");
            case EmailEventType.Deferred:
                return GetText("EmailLogs_Defered");
            case EmailEventType.Blocked:
                return GetText("EmailLogs_Blocked");
            case EmailEventType.Error:
                return GetText("EmailLogs_Error");
            case EmailEventType.Invalid:
                return GetText("EmailLogs_Invalid");
            case EmailEventType.Unsubscribed:
            case EmailEventType.Unsubscription:
                return GetText("EmailLogs_Unsubscribed");
            case EmailEventType.Accepted:
                return GetText("EmailLogs_Accepted");
            case EmailEventType.Rejected:
                return GetText("EmailLogs_Rejected");
            case EmailEventType.Skipped:
                return GetText("EmailLogs_Skipped");
            case EmailEventType.Replies:
                return GetText("EmailLogs_Replies");
            default:
                return event?.toString();
        }
    }

    const renderTimeline = (record: WebApiEmailDtoCommunicationReportDto) => {
        const events = [record.event!, ...(record.subEvents ?? [])];
        return <div style={{ margin: 0 }}>
            {events.map((p, index) =>
                <div key={index} style={{ marginBottom: 4 }}>
                    {formatDateString(p.date!.toString())} <Tag color={mapColor(p.event as EmailEventType)}>{mapText(p.event as EmailEventType)}</Tag>{p.reason ? <span>{p.reason}</span> : null}
                </div>)}
        </div>;
    };

    return <>
        <Table
            loading={props.isLoading}
            columns={columns}
            locale={{ emptyText: GetText("EmailLogs_EmptyMessage") }}
            rowKey={m => m.event?.messageId!}
            pagination={{
                position: ['topRight', 'bottomRight'],
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100],
                showTotal: (total, range) => `${range[0]}-${range[1]} ${GetText("EmailLogs_PaginationOf")} ${total}`,
            }}
            expandable={{
                expandedRowRender: renderTimeline,
            }}
            dataSource={props.logs}
        />

        <Modal
            title={GetText("EmailLogs_Details")}
            visible={isModalVisible}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={handleOk}
            width={900}
            onOk={handleOk}
        >

            {mailContentLoading ? <Spin /> : selectedLog && (
                <div>
                    <p>
                        <b>{GetText("EmailLogs_Email")}:</b> {selectedLog.event?.email}
                    </p>
                    <p>
                        <b>{GetText("EmailLogs_Subject")}:</b> {selectedLog.event?.subject}
                    </p>
                    <p>
                        <b>{GetText("EmailLogs_From")}:</b> {selectedLog.event?.from}
                    </p>
                    {selectedLog.event?.reason ? <p>
                        <b>{GetText("EmailLogs_Reason")}:</b> {selectedLog.event?.reason}
                    </p> : null}
                    <div
                        className="email-content-wrapper"
                        style={{
                            border: "2px solid #f0f0f0",
                            padding: 16,
                            marginTop: 12,
                            maxHeight: 400,
                            overflowY: "auto",
                            overflowX: "hidden"
                        }}
                    >
                        {emailContent ? (
                            <div
                                className="email-content"
                                dangerouslySetInnerHTML={{ __html: emailContent.htmlContent! }}
                            />
                        ) : (
                            <p>Loading content...</p>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    </>
};

export default AdminEmailLogsTable;